import './character.css';
import { values, type Character as CharacterConfig } from './config';

/**
 * A character, drawn.
 *
 * ## The shapes are machine-converted, and that is on purpose
 *
 * The body of this file comes from the generator in `dev/` - four hundred and
 * four shapes across a hundred and sixty-five conditionals, over two views -
 * transformed from its template language into JSX by a script rather than
 * retyped. Retyping it would have meant reading several thousand path
 * coordinates by eye, where a single wrong digit is a hairstyle that renders
 * slightly wrong for one person and nobody ever finds out why.
 *
 * That script is `scripts/convert-character.mjs`, and it re-ran for the v1.1
 * drop in `dev/character-v1.1` - which is why that was five new hairstyles
 * rather than five days. It was checked the way it says to check it: convert
 * the *old* generator first and confirm the result comes back identical to what
 * is already here, then point it at the new one.
 *
 * So it reads like generated code, because it is. The parts worth editing by
 * hand are in `config.ts`: the palettes, the option lists and the arithmetic
 * the drawing reads.
 *
 * ## Two crops, because a body is not an avatar
 *
 * The full drawing is a standing figure, 220 by 272. At the size a crowd needs
 * - thirty-two pixels - a whole body is an unreadable speck, so `bust` shows
 * the head and shoulders square, which is the only version that survives being
 * small. `full` is for the maker, where the whole character is the point.
 */
const CROP = {
  /*
   * Head and shoulders, square, so it drops into any avatar slot.
   *
   * The frame reaches to y=2 because hats do: a beanie or a cap sits above the
   * hairline, and a tighter crop sliced the top off every character wearing
   * one - which is a good third of them.
   */
  bust: '36 2 148 148',
  full: '0 0 220 272',
} as const;

export function Character({
  config,
  size = 32,
  crop = 'bust',
  view = 'front',
  anim = 'idle',
  stagger = 0,
  className = '',
}: {
  config: CharacterConfig;
  /** Rendered pixel size. Square for `bust`, portrait for `full`. */
  size?: number;
  crop?: keyof typeof CROP;
  /**
   * Which side of them you are looking at.
   *
   * Both come off one skeleton - same view box, same flags, same arithmetic -
   * so the back is a different set of shapes rather than a different character.
   * It exists for the maker, where somebody choosing a ponytail wants to see
   * the ponytail.
   */
  view?: 'front' | 'back';
  /**
   * What they are doing. `idle` is a slow breath, which is what somebody
   * standing in a room does - a perfectly still figure reads as a picture of a
   * person rather than a person.
   */
  anim?: string;
  /**
   * Which of two animation offsets to use, so a crowd is not a chorus line.
   * Anything outside 1 or 2 runs on the beat.
   */
  stagger?: number;
  className?: string;
}) {
  const v = values(config);

  return (
    <svg
      viewBox={CROP[crop]}
      width={size}
      height={crop === 'full' ? Math.round((size * 272) / 220) : size}
      className={['a-' + anim, stagger === 1 ? 'crowd-d1' : stagger === 2 ? 'crowd-d2' : '', className]
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-hidden="true"
      /*
       * `hidden` is what makes the bust crop a crop.
       *
       * The generator sets `overflow: visible` so its animations can swing
       * outside the frame, and inherited here that meant the viewBox narrowed
       * to a head still painted the whole body around it - a thirty-two pixel
       * avatar that was a shrunken standing figure rather than a face.
       */
      style={{ display: 'block', overflow: crop === 'full' ? 'visible' : 'hidden' }}
    >
      {view === 'back' ? (
        <>

            <ellipse cx="110" cy="252" rx="48" ry="9" fill="#000" opacity="0.28" />
            <g className="rig" stroke="#241f26" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round">
              <g id="body-back">
                <rect x="84" y="192" width="22" height="46" rx="6" fill={v.legFill} />
                <rect x="114" y="192" width="22" height="46" rx="6" fill={v.legFill} />
                {v.lShorts ? (<>
                  <rect x="84" y="214" width="22" height="26" rx="6" fill={v.skin} />
                  <rect x="114" y="214" width="22" height="26" rx="6" fill={v.skin} />
                </>) : null}
                {v.lSkirt ? (<>
                  <rect x="84" y="198" width="22" height="42" rx="6" fill={v.skin} />
                  <rect x="114" y="198" width="22" height="42" rx="6" fill={v.skin} />
                </>) : null}
                {v.lJogger ? (<>
                  <path d="M82 222 h26 v14 h-26 z M112 222 h26 v14 h-26 z" fill={v.inner} />
                </>) : null}
                <path d="M74 232 h34 a6 6 0 0 1 6 6 v8 h-46 v-8 a6 6 0 0 1 6 -6 z" fill={v.shoe} />
                <path d="M112 232 h34 a6 6 0 0 1 6 6 v8 h-46 v-8 a6 6 0 0 1 6 -6 z" fill={v.shoe} />
                <rect x="72" y="144" width="76" height="60" rx="14" fill={v.outfit} />
                <path d="M88 146 L110 158 L132 146" fill="none" stroke="#241f26" strokeWidth="4" />
                {v.lSkirt ? (<>
                  <path d="M76 184 L144 184 L154 212 L66 212 Z" fill={v.skirtFill} />
                </>) : null}
                <g className="arm-l">
                  <rect x="60" y="150" width="17" height="48" rx="8.5" fill={v.skin} />
                  {v.sleeveLong ? (<>
                    <rect x="60" y="150" width="17" height="48" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.sleeveShort ? (<>
                    <rect x="60" y="150" width="17" height="25" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  <circle cx="68.5" cy="203" r="10" fill={v.skin} />
                </g>
                <g className="arm-r">
                  <rect x="143" y="150" width="17" height="48" rx="8.5" fill={v.skin} />
                  {v.sleeveLong ? (<>
                    <rect x="143" y="150" width="17" height="48" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.sleeveShort ? (<>
                    <rect x="143" y="150" width="17" height="25" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.iGlow ? (<>
                    <rect x="147" y="172" width="9" height="38" rx="4.5" fill="#8dff6a" />
                  </>) : null}
                  {v.iPhone ? (<>
                    <rect x="143" y="174" width="17" height="28" rx="3.5" fill="#2a2630" />
                    <rect x="146" y="177" width="11" height="18" rx="2" fill="#bfe0ff" stroke="none" />
                  </>) : null}
                  {v.iCup ? (<>
                    <path d="M144 178 L160 178 L157 202 L147 202 Z" fill="#f2ece2" />
                    <path d="M150 178 L154 166" fill="none" stroke="#241f26" strokeWidth="3" />
                  </>) : null}
                  {v.iVinyl ? (<>
                    <circle cx="158" cy="184" r="16" fill="#1c1a20" />
                    <circle cx="158" cy="184" r="6" fill={v.accent} stroke="none" />
                    <circle cx="158" cy="184" r="1.6" fill="#8d8598" stroke="none" />
                  </>) : null}
                  {v.iMic ? (<>
                    <rect x="147" y="176" width="9" height="26" rx="4.5" fill="#2a2630" />
                    <circle cx="151.5" cy="171" r="8" fill="#8d8598" />
                  </>) : null}
                  {v.jWrist ? (<>
                    <rect x="143" y="188" width="17" height="7" rx="3" fill={v.accent} />
                  </>) : null}
                  <circle cx="151.5" cy="203" r="10" fill={v.skin} />
                </g>
                {v.oHoodie ? (<>
                  <path d="M74 146 C84 174 136 174 146 146" fill={v.inner} stroke="#241f26" strokeWidth="4" />
                </>) : null}
                {v.oTank ? (<>
                  <path d="M72 144 L90 144 C90 158 84 166 74 168 C72 158 72 150 72 144 Z" fill={v.skin} />
                  <path d="M148 144 L130 144 C130 158 136 166 146 168 C148 158 148 150 148 144 Z" fill={v.skin} />
                </>) : null}
                {v.oJersey ? (<>
                  <path d="M76 152 v48 M144 152 v48" stroke={v.inner} strokeWidth="5" />
                  <text x="110" y="194" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontSize="34" fontWeight="700" fill={v.inner} stroke="none">7</text>
                </>) : null}
                {v.oFlannel ? (<>
                  <path d="M86 146 v56 M110 146 v56 M134 146 v56" stroke={v.inner} strokeWidth="4" />
                  <path d="M74 164 h72 M74 186 h72" stroke={v.inner} strokeWidth="4" />
                </>) : null}
                {v.oVarsity ? (<>
                  <path d="M75 195 h70" stroke={v.inner} strokeWidth="12" />
                  <path d="M61 190 h15 M144 190 h15" stroke={v.inner} strokeWidth="11" />
                </>) : null}
                {v.oBlazer ? (<>
                  <path d="M110 150 v52" stroke={v.inner} strokeWidth="4" />
                </>) : null}
                {v.oBand ? (<>
                  <path d="M104 160 L118 160 L110 172 L117 172 L102 192 L107 177 L100 177 Z" fill={v.accent} strokeWidth="3" />
                </>) : null}
                {v.oLeather ? (<>
                  <path d="M84 152 C92 160 128 160 136 152" fill="none" stroke="#c9cdd6" strokeWidth="3" />
                </>) : null}
                {v.oTiedye ? (<>
                  <circle cx="110" cy="176" r="20" fill="#ffffff" opacity="0.16" stroke="none" />
                  <circle cx="110" cy="176" r="12" fill="#ffffff" opacity="0.24" stroke="none" />
                  <circle cx="110" cy="176" r="5" fill="#ffffff" opacity="0.35" stroke="none" />
                </>) : null}
                {v.oTrack ? (<>
                  <path d="M74 162 h72" stroke="#f4efe6" strokeWidth="4" />
                </>) : null}
              </g>
              <g className="head-sync">
                <g id="head-back">
                  <circle cx="58" cy="116" r="8" fill={v.skin} />
                  <circle cx="162" cy="116" r="8" fill={v.skin} />
                  <rect x="58" y="46" width="104" height="98" rx="34" fill={v.skin} />
                  {v.jEar ? (<>
                    <circle cx="58" cy="127" r="3.5" fill="#e8c15a" strokeWidth="3" />
                    <circle cx="162" cy="127" r="3.5" fill="#e8c15a" strokeWidth="3" />
                  </>) : null}
                </g>
                <g id="hair-back-view" fill={v.hair}>
                  {v.hLiberty ? (<>
                    <path d="M54 92 C54 74 58 60 66 50 L52 16 L80 42 L92 8 L102 40 L110 2 L118 40 L128 8 L140 42 L168 16 L154 50 C162 60 166 74 166 92 C166 108 162 124 154 134 C136 126 84 126 66 134 C58 124 54 108 54 92 Z" />
                  </>) : null}
                  {v.hAfro ? (<>
                    <path d="M110 6 C66 6 38 36 42 74 C44 104 58 126 78 134 C98 140 122 140 142 134 C162 126 176 104 178 74 C182 36 154 6 110 6 Z" />
                  </>) : null}
                  {v.hSbuns ? (<>
                    <circle cx="72" cy="28" r="17" />
                    <circle cx="148" cy="28" r="17" />
                    <path d="M110 26 C68 26 50 54 52 96 C54 124 62 146 70 160 C84 150 136 150 150 160 C158 146 166 124 168 96 C170 54 152 26 110 26 Z" />
                  </>) : null}
                  {v.hBraids ? (<>
                    <path d="M110 26 C68 26 50 54 52 96 C54 124 62 146 70 160 C84 150 136 150 150 160 C158 146 166 124 168 96 C170 54 152 26 110 26 Z" />
                    <circle cx="76" cy="152" r="11" />
                    <circle cx="74" cy="174" r="10" />
                    <circle cx="72" cy="194" r="9" />
                    <circle cx="144" cy="152" r="11" />
                    <circle cx="146" cy="174" r="10" />
                    <circle cx="148" cy="194" r="9" />
                  </>) : null}
                  {v.hHalf ? (<>
                    <path d="M110 24 C72 24 56 50 56 86 C56 104 60 118 66 128 C84 120 136 120 154 128 C160 118 164 104 164 86 C164 50 148 24 110 24 Z" />
                    <path d="M152 74 C166 96 166 132 156 156 C148 164 136 160 140 148 C148 126 146 98 138 82 Z" />
                  </>) : null}
                  {v.hSwept ? (<>
                    <path d="M110 20 C62 20 38 56 42 100 C44 132 52 158 58 182 C62 192 74 190 74 176 L74 154 L86 166 L98 152 L110 168 L122 152 L134 166 L146 154 L146 176 C146 190 158 192 162 182 C168 158 176 132 178 100 C182 56 158 20 110 20 Z" />
                  </>) : null}
                  {v.hSpiky ? (<>
                    <path d="M110 22 C64 22 44 52 46 94 C48 118 54 138 60 160 L74 140 L86 166 L98 140 L110 168 L122 140 L134 166 L146 140 L160 160 C166 138 172 118 174 94 C176 52 156 22 110 22 Z" />
                  </>) : null}
                  {v.hQuiff ? (<>
                    <path d="M110 26 C72 26 56 52 56 88 C56 112 62 130 68 144 C86 138 134 138 152 144 C158 130 164 112 164 88 C164 52 148 26 110 26 Z" />
                  </>) : null}
                  {v.hUnder ? (<>
                    <path d="M110 26 C74 26 58 48 58 80 C58 96 62 108 66 118 C86 112 134 112 154 118 C158 108 162 96 162 80 C162 48 146 26 110 26 Z" />
                  </>) : null}
                  {v.hBuzz ? (<>
                    <path d="M110 28 C72 28 56 52 56 88 C56 112 64 132 78 141 C90 136 130 136 142 141 C156 132 164 112 164 88 C164 52 148 28 110 28 Z" />
                  </>) : null}
                  {v.hSlick ? (<>
                    <path d="M110 24 C72 24 56 50 56 86 C56 110 62 130 76 140 C90 135 130 135 144 140 C158 130 164 110 164 86 C164 50 148 24 110 24 Z" />
                  </>) : null}
                  {v.hCurtain ? (<>
                    <path d="M110 24 C72 24 54 50 54 88 C54 114 62 134 76 146 C90 140 130 140 144 146 C158 134 166 114 166 88 C166 50 148 24 110 24 Z" />
                  </>) : null}
                  {v.hDreads ? (<>
                    <path d="M110 24 C70 24 50 52 52 92 C54 114 60 128 66 138 C86 130 134 130 154 138 C160 128 166 114 168 92 C170 52 150 24 110 24 Z" />
                    <rect x="44" y="104" width="13" height="52" rx="6.5" />
                    <rect x="62" y="112" width="13" height="56" rx="6.5" />
                    <rect x="80" y="116" width="13" height="52" rx="6.5" />
                    <rect x="98" y="118" width="13" height="56" rx="6.5" />
                    <rect x="116" y="116" width="13" height="52" rx="6.5" />
                    <rect x="134" y="112" width="13" height="56" rx="6.5" />
                    <rect x="152" y="104" width="13" height="52" rx="6.5" />
                  </>) : null}
                  {v.hRows ? (<>
                    <path d="M110 26 C74 26 56 50 56 88 C56 110 62 130 76 140 C90 135 130 135 144 140 C158 130 164 110 164 88 C164 50 146 26 110 26 Z" />
                    <path d="M76 137 C70 104 74 62 88 34 M94 139 C90 104 96 60 104 32 M126 139 C130 104 124 60 116 32 M144 137 C150 104 146 62 132 34" fill="none" stroke={v.hairLight} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
                  </>) : null}
                  {v.hLong ? (<>
                    <path d="M110 20 C60 20 36 56 40 100 C42 130 46 156 52 178 C70 170 150 170 168 178 C174 156 178 130 180 100 C184 56 160 20 110 20 Z" />
                  </>) : null}
                  {v.hMessy ? (<>
                    <path d="M110 22 C64 22 42 54 46 96 C48 122 54 140 62 156 L72 138 L84 158 L96 136 L110 160 L124 136 L136 158 L148 138 L158 156 C166 140 172 122 174 96 C178 54 156 22 110 22 Z" />
                  </>) : null}
                  {v.hFlat ? (<>
                    <path d="M56 32 C56 26 60 22 66 22 L154 22 C160 22 164 26 164 32 L164 88 C164 112 158 132 146 141 C130 135 90 135 74 141 C62 132 56 112 56 88 Z" />
                  </>) : null}
                  {v.hMullet ? (<>
                    <path d="M110 26 C74 26 58 50 58 86 C58 108 62 124 68 136 C64 152 64 168 70 180 C88 174 132 174 150 180 C156 168 156 152 152 136 C158 124 162 108 162 86 C162 50 146 26 110 26 Z" />
                  </>) : null}
                  {v.hBald ? (<>
                    <path d="M60 84 C58 112 64 136 72 150 C90 142 130 142 148 150 C156 136 162 112 160 84 C154 108 140 120 110 120 C80 120 66 108 60 84 Z" />
                  </>) : null}
                  {v.hMohawk ? (<>
                    <path d="M94 18 C84 44 82 80 88 116 L132 116 C138 80 136 44 126 18 C120 30 100 30 94 18 Z" />
                  </>) : null}
                  {v.hCurls ? (<>
                    <path d="M110 14 C86 12 70 20 62 34 C44 40 32 58 36 78 C28 94 34 114 48 122 C52 140 68 152 88 152 C102 160 120 158 130 152 C152 152 166 138 170 122 C184 114 190 94 182 78 C186 58 174 40 156 34 C148 18 132 12 110 14 Z" />
                    <path d="M56 60 C70 46 90 42 104 50 M120 40 C138 42 154 56 160 74 M46 100 C50 86 60 74 74 66 M152 102 C162 88 164 72 156 60 M90 26 C104 18 122 18 134 28 M74 134 C88 142 106 146 120 140" fill="none" stroke={v.hairLight} strokeWidth="5" strokeLinecap="round" opacity="0.45" />
                  </>) : null}
                  {v.hBun ? (<>
                    <circle cx="110" cy="24" r="23" />
                    <path d="M110 26 C68 26 50 54 52 96 C54 124 62 146 70 160 C84 150 136 150 150 160 C158 146 166 124 168 96 C170 54 152 26 110 26 Z" />
                  </>) : null}
                  {v.hTail ? (<>
                    <path d="M110 22 C66 22 46 52 48 96 C50 124 58 142 66 154 C82 148 96 150 100 158 C96 182 96 202 102 216 C106 224 118 224 122 216 C128 202 128 182 124 158 C128 150 142 148 154 154 C162 142 170 124 172 96 C174 52 154 22 110 22 Z" />
                  </>) : null}
                </g>
                <g id="mask-back">
                  {v.mMouse ? (<>
                    <circle cx="66" cy="52" r="20" fill={v.maskFill} />
                    <circle cx="154" cy="52" r="20" fill={v.maskFill} />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                  </>) : null}
                  {v.mCat ? (<>
                    <path d="M60 66 L54 26 L86 46 Z" fill={v.maskFill} />
                    <path d="M160 66 L166 26 L134 46 Z" fill={v.maskFill} />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                  </>) : null}
                  {v.mBear ? (<>
                    <circle cx="70" cy="54" r="17" fill={v.maskFill} />
                    <circle cx="150" cy="54" r="17" fill={v.maskFill} />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                  </>) : null}
                  {v.mFrog ? (<>
                    <circle cx="80" cy="46" r="17" fill={v.maskFill} />
                    <circle cx="140" cy="46" r="17" fill={v.maskFill} />
                    <rect x="58" y="52" width="104" height="92" rx="34" fill={v.maskFill} />
                  </>) : null}
                  {v.mRobot ? (<>
                    <path d="M110 22 v14" stroke="#8d8598" strokeWidth="4" />
                    <circle cx="110" cy="18" r="6" fill={v.accent} />
                    <rect x="58" y="46" width="104" height="98" rx="18" fill={v.maskFill} />
                    <rect x="52" y="96" width="10" height="22" rx="5" fill="#8d8598" />
                    <rect x="158" y="96" width="10" height="22" rx="5" fill="#8d8598" />
                    <rect x="88" y="70" width="44" height="34" rx="6" fill="#241f26" />
                    <path d="M96 78 h28 M96 86 h28 M96 94 h28" stroke="#3a4a5c" strokeWidth="3" />
                  </>) : null}
                </g>
                <g id="accessory-back">
                  {v.aBucket ? (<>
                    <path d="M66 64 C66 40 86 28 110 28 C134 28 154 40 154 64 Z" fill={v.accent} />
                    <path d="M54 64 C78 56 142 56 166 64 L158 82 C134 74 86 74 62 82 Z" fill={v.accent} />
                  </>) : null}
                  {v.aBandana ? (<>
                    <path d="M62 72 C88 64 132 64 158 72 L158 86 C132 78 88 78 62 86 Z" fill={v.accent} />
                    <circle cx="110" cy="90" r="6" fill={v.accent} />
                    <path d="M104 94 L98 112 L106 106 Z M116 94 L122 112 L114 106 Z" fill={v.accent} />
                  </>) : null}
                  {v.aBand ? (<>
                    <path d="M60 82 C88 74 132 74 160 82 L160 94 C132 86 88 86 60 94 Z" fill="#f4efe6" />
                  </>) : null}
                  {v.aCap ? (<>
                    <path d="M56 88 C56 52 78 34 110 34 C142 34 164 52 164 88 C140 78 80 78 56 88 Z" fill={v.accent} />
                    <path d="M96 72 h28 v16 h-28 z" fill={v.inner} />
                  </>) : null}
                  {v.aBeanie ? (<>
                    <path d="M56 88 C56 50 78 30 110 30 C142 30 164 50 164 88 Z" fill={v.accent} />
                    <rect x="52" y="84" width="116" height="18" rx="8" fill={v.inner} />
                    <circle cx="110" cy="26" r="9" fill={v.inner} />
                  </>) : null}
                  {v.aPhones ? (<>
                    <path d="M56 100 C54 52 80 28 110 28 C140 28 166 52 164 100" fill="none" stroke="#241f26" strokeWidth="14" />
                    <path d="M56 100 C54 52 80 28 110 28 C140 28 166 52 164 100" fill="none" stroke={v.accent} strokeWidth="7" />
                    <rect x="44" y="98" width="24" height="38" rx="10" fill={v.accent} />
                    <rect x="152" y="98" width="24" height="38" rx="10" fill={v.accent} />
                  </>) : null}
                </g>
              </g>
            </g>
        </>
      ) : (
        <>

            <ellipse cx="110" cy="252" rx="48" ry="9" fill="#000" opacity="0.28" />
            <g className="rig" stroke="#241f26" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round">
              <g id="hair-back" className="head-sync" fill={v.hairDark}>
                {v.hSbuns ? (<>
                  <circle cx="72" cy="28" r="17" />
                  <circle cx="148" cy="28" r="17" />
                  <path d="M110 26 C68 26 50 54 52 96 L64 104 L64 86 C64 60 84 46 110 46 C136 46 156 60 156 86 L156 104 L168 96 C170 54 152 26 110 26 Z" />
                </>) : null}
                {v.hBraids ? (<>
                  <path d="M110 26 C68 26 50 54 52 96 L64 104 L64 86 C64 60 84 46 110 46 C136 46 156 60 156 86 L156 104 L168 96 C170 54 152 26 110 26 Z" />
                  <circle cx="62" cy="120" r="11" />
                  <circle cx="60" cy="142" r="10" />
                  <circle cx="58" cy="162" r="9" />
                  <circle cx="158" cy="120" r="11" />
                  <circle cx="160" cy="142" r="10" />
                  <circle cx="162" cy="162" r="9" />
                </>) : null}
                {v.hSwept ? (<>
                  <path d="M110 20 C62 20 38 56 42 100 C44 128 50 152 56 180 C60 190 72 188 72 174 L72 96 C72 70 87 52 110 52 C133 52 148 70 148 96 L148 174 C148 188 160 190 164 180 C170 152 176 128 178 100 C182 56 158 20 110 20 Z" />
                </>) : null}
                {v.hSpiky ? (<>
                  <path d="M110 22 C64 22 44 52 46 94 L60 104 L60 84 C60 58 82 44 110 44 C138 44 160 58 160 84 L160 104 L174 94 C176 52 156 22 110 22 Z" />
                </>) : null}
                {v.hQuiff ? (<>
                  <path d="M110 26 C74 26 56 52 56 88 L62 96 L62 84 C62 60 84 48 110 48 C136 48 158 60 158 84 L158 96 L164 88 C164 52 146 26 110 26 Z" />
                </>) : null}
                {v.hUnder ? (<>
                  <path d="M110 26 C76 26 60 48 60 76 L66 84 L66 74 C66 56 86 46 110 46 C134 46 154 56 154 74 L154 84 L160 76 C160 48 144 26 110 26 Z" />
                </>) : null}
                {v.hBuzz ? (<>
                  <path d="M110 28 C72 28 56 52 56 88 L62 96 L62 86 C62 62 82 48 110 48 C138 48 158 62 158 86 L158 96 L164 88 C164 52 148 28 110 28 Z" />
                </>) : null}
                {v.hSlick ? (<>
                  <path d="M110 24 C72 24 56 50 56 86 L62 96 L62 82 C62 58 84 46 110 46 C136 46 158 58 158 82 L158 96 L164 86 C164 50 148 24 110 24 Z" />
                </>) : null}
                {v.hCurtain ? (<>
                  <path d="M110 24 C72 24 54 50 54 88 L62 100 L62 82 C62 58 84 46 110 46 C136 46 158 58 158 82 L158 100 L166 88 C166 50 148 24 110 24 Z" />
                </>) : null}
                {v.hDreads ? (<>
                  <path d="M110 24 C70 24 50 52 52 92 L64 100 L64 84 C64 60 86 48 110 48 C134 48 156 60 156 84 L156 100 L168 92 C170 52 150 24 110 24 Z" />
                </>) : null}
                {v.hRows ? (<>
                  <path d="M110 26 C74 26 56 50 56 88 L62 98 L62 84 C62 60 84 48 110 48 C136 48 158 60 158 84 L158 98 L164 88 C164 50 146 26 110 26 Z" />
                </>) : null}
                {v.hLong ? (<>
                  <path d="M110 20 C60 20 36 56 40 100 C42 130 48 170 54 200 C58 210 74 208 74 194 L74 96 C74 70 88 52 110 52 C132 52 146 70 146 96 L146 194 C146 208 162 210 166 200 C172 170 178 130 180 100 C184 56 160 20 110 20 Z" />
                </>) : null}
                {v.hMessy ? (<>
                  <path d="M110 22 C64 22 42 54 46 96 L60 106 L60 84 C60 58 82 44 110 44 C138 44 160 58 160 84 L160 106 L174 96 C178 54 156 22 110 22 Z" />
                </>) : null}
                {v.hFlat ? (<>
                  <path d="M64 22 L156 22 C160 22 164 26 164 32 L164 92 L158 98 L158 84 C158 60 136 48 110 48 C84 48 62 60 62 84 L62 98 L56 92 L56 32 C56 26 60 22 64 22 Z" />
                </>) : null}
                {v.hMullet ? (<>
                  <path d="M110 26 C74 26 58 50 58 86 L62 96 L62 82 C62 58 84 46 110 46 C136 46 158 58 158 82 L158 96 L162 86 C162 50 146 26 110 26 Z" />
                  <path d="M64 118 C56 148 58 180 66 198 C74 204 86 200 82 190 C74 168 74 142 80 122 Z" />
                  <path d="M156 118 C164 148 162 180 154 198 C146 204 134 200 138 190 C146 168 146 142 140 122 Z" />
                </>) : null}
                {v.hCurls ? (<>
                  <path d="M110 14 C86 12 70 20 62 34 C42 40 30 60 34 82 C24 100 32 124 48 134 C52 156 70 170 92 172 C104 180 122 178 132 170 C154 168 170 152 174 132 C190 122 196 98 186 80 C190 58 176 38 156 32 C148 18 132 12 110 14 Z" />
                </>) : null}
                {v.hBun ? (<>
                  <circle cx="110" cy="24" r="23" />
                  <path d="M110 26 C68 26 50 54 52 96 L64 104 L64 86 C64 60 84 46 110 46 C136 46 156 60 156 86 L156 104 L168 96 C170 54 152 26 110 26 Z" />
                </>) : null}
                {v.hTail ? (<>
                  <path d="M158 96 C182 112 186 154 174 180 C166 190 152 186 156 172 C166 148 164 118 152 104 Z" />
                  <path d="M110 22 C66 22 46 52 48 96 L62 106 L62 84 C62 60 84 46 110 46 C136 46 158 60 158 84 L158 106 L172 96 C174 52 154 22 110 22 Z" />
                </>) : null}
              </g>
              <g id="body">
                <rect x="84" y="192" width="22" height="46" rx="6" fill={v.legFill} />
                <rect x="114" y="192" width="22" height="46" rx="6" fill={v.legFill} />
                {v.lShorts ? (<>
                  <rect x="84" y="214" width="22" height="26" rx="6" fill={v.skin} />
                  <rect x="114" y="214" width="22" height="26" rx="6" fill={v.skin} />
                </>) : null}
                {v.lSkirt ? (<>
                  <rect x="84" y="198" width="22" height="42" rx="6" fill={v.skin} />
                  <rect x="114" y="198" width="22" height="42" rx="6" fill={v.skin} />
                </>) : null}
                {v.lJogger ? (<>
                  <path d="M82 222 h26 v14 h-26 z M112 222 h26 v14 h-26 z" fill={v.inner} />
                </>) : null}
                <path d="M78 232 h30 a8 8 0 0 1 8 8 v6 h-46 v-6 a8 8 0 0 1 8 -8 z" fill={v.shoe} />
                <path d="M112 232 h30 a8 8 0 0 1 8 8 v6 h-46 v-6 a8 8 0 0 1 8 -8 z" fill={v.shoe} />
                <rect x="72" y="144" width="76" height="60" rx="14" fill={v.outfit} />
                {v.lSkirt ? (<>
                  <path d="M76 184 L144 184 L154 212 L66 212 Z" fill={v.skirtFill} />
                </>) : null}
                <g className="arm-l">
                  <rect x="60" y="150" width="17" height="48" rx="8.5" fill={v.skin} />
                  {v.sleeveLong ? (<>
                    <rect x="60" y="150" width="17" height="48" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.sleeveShort ? (<>
                    <rect x="60" y="150" width="17" height="25" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  <circle cx="68.5" cy="203" r="10" fill={v.skin} />
                </g>
                <g className="arm-r">
                  <rect x="143" y="150" width="17" height="48" rx="8.5" fill={v.skin} />
                  {v.sleeveLong ? (<>
                    <rect x="143" y="150" width="17" height="48" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.sleeveShort ? (<>
                    <rect x="143" y="150" width="17" height="25" rx="8.5" fill={v.sleeveFill} />
                  </>) : null}
                  {v.iGlow ? (<>
                    <rect x="147" y="172" width="9" height="38" rx="4.5" fill="#8dff6a" />
                  </>) : null}
                  {v.iPhone ? (<>
                    <rect x="143" y="174" width="17" height="28" rx="3.5" fill="#2a2630" />
                    <rect x="146" y="177" width="11" height="18" rx="2" fill="#bfe0ff" stroke="none" />
                  </>) : null}
                  {v.iCup ? (<>
                    <path d="M144 178 L160 178 L157 202 L147 202 Z" fill="#f2ece2" />
                    <path d="M150 178 L154 166" fill="none" stroke="#241f26" strokeWidth="3" />
                  </>) : null}
                  {v.iVinyl ? (<>
                    <circle cx="158" cy="184" r="16" fill="#1c1a20" />
                    <circle cx="158" cy="184" r="6" fill={v.accent} stroke="none" />
                    <circle cx="158" cy="184" r="1.6" fill="#8d8598" stroke="none" />
                  </>) : null}
                  {v.iMic ? (<>
                    <rect x="147" y="176" width="9" height="26" rx="4.5" fill="#2a2630" />
                    <circle cx="151.5" cy="171" r="8" fill="#8d8598" />
                  </>) : null}
                  {v.jWrist ? (<>
                    <rect x="143" y="188" width="17" height="7" rx="3" fill={v.accent} />
                  </>) : null}
                  <circle cx="151.5" cy="203" r="10" fill={v.skin} />
                </g>
                {v.oJacket ? (<>
                  <path d="M96 144 h28 v56 h-28 z" fill={v.inner} stroke="none" />
                  <path d="M96 144 v56 M124 144 v56" stroke="#241f26" strokeWidth="4" />
                  <path d="M92 146 h36 a7 7 0 0 1 7 7 v6 a7 7 0 0 1 -7 7 h-36 a7 7 0 0 1 -7 -7 v-6 a7 7 0 0 1 7 -7 z" fill={v.accent} />
                  <path d="M100 147 v18 M110 147 v18 M120 147 v18" stroke="#f4efe6" strokeWidth="3" />
                </>) : null}
                {v.oHoodie ? (<>
                  <path d="M74 146 C84 172 136 172 146 146" fill={v.inner} stroke="#241f26" strokeWidth="4" />
                  <path d="M101 166 v16 M119 166 v16" fill="none" stroke="#241f26" strokeWidth="3" />
                  <rect x="86" y="180" width="48" height="20" rx="7" fill={v.pocket} />
                </>) : null}
                {v.oTee ? (<>
                  <path d="M99 144 C103 158 117 158 121 144" fill={v.skin} stroke="#241f26" strokeWidth="4" />
                  <circle cx="110" cy="180" r="12" fill={v.accent} />
                </>) : null}
                {v.oTank ? (<>
                  <path d="M72 144 L90 144 C90 161 84 170 74 172 C72 162 72 152 72 144 Z" fill={v.skin} />
                  <path d="M148 144 L130 144 C130 161 136 170 146 172 C148 162 148 152 148 144 Z" fill={v.skin} />
                  <path d="M98 144 C102 161 118 161 122 144 Z" fill={v.skin} />
                </>) : null}
                {v.oBlazer ? (<>
                  <path d="M94 144 L110 176 L126 144 Z" fill="#f2ece2" />
                  <path d="M110 166 L100 177 L110 202 L120 177 Z" fill={v.accent} strokeWidth="4" />
                  <path d="M94 144 L110 176 M126 144 L110 176" stroke="#241f26" strokeWidth="4" />
                </>) : null}
                {v.oJersey ? (<>
                  <path d="M97 144 C101 160 119 160 123 144" fill={v.inner} stroke="#241f26" strokeWidth="4" />
                  <path d="M76 152 v48 M144 152 v48" stroke={v.inner} strokeWidth="5" />
                  <text x="110" y="194" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontSize="30" fontWeight="700" fill={v.inner} stroke="none">7</text>
                </>) : null}
                {v.oFlannel ? (<>
                  <path d="M86 146 v56 M110 146 v56 M134 146 v56" stroke={v.inner} strokeWidth="4" />
                  <path d="M74 160 h72 M74 182 h72" stroke={v.inner} strokeWidth="4" />
                  <path d="M104 144 h12 v58 h-12 z" fill={v.inner} stroke="none" />
                  <path d="M104 144 v58 M116 144 v58" stroke="#241f26" strokeWidth="3" />
                </>) : null}
                {v.oVarsity ? (<>
                  <path d="M96 144 C100 158 120 158 124 144" fill={v.inner} stroke="#241f26" strokeWidth="4" />
                  <path d="M75 195 h70" stroke={v.inner} strokeWidth="12" />
                  <path d="M61 190 h15 M144 190 h15" stroke={v.inner} strokeWidth="11" />
                  <text x="110" y="186" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontSize="32" fontWeight="700" fill={v.inner} stroke="none">G</text>
                </>) : null}
                {v.oBand ? (<>
                  <path d="M99 144 C103 158 117 158 121 144" fill={v.skin} stroke="#241f26" strokeWidth="4" />
                  <path d="M103 158 L121 158 L111 172 L120 172 L99 196 L106 177 L97 177 Z" fill={v.accent} strokeWidth="3" />
                </>) : null}
                {v.oLeather ? (<>
                  <path d="M96 144 L110 158 L124 144" fill="none" stroke="#241f26" strokeWidth="4" />
                  <path d="M110 158 L100 202" fill="none" stroke="#c9cdd6" strokeWidth="3.5" />
                  <circle cx="82" cy="152" r="2.2" fill="#c9cdd6" stroke="none" />
                  <circle cx="90" cy="150" r="2.2" fill="#c9cdd6" stroke="none" />
                  <circle cx="130" cy="150" r="2.2" fill="#c9cdd6" stroke="none" />
                  <circle cx="138" cy="152" r="2.2" fill="#c9cdd6" stroke="none" />
                </>) : null}
                {v.oTiedye ? (<>
                  <path d="M99 144 C103 158 117 158 121 144" fill={v.skin} stroke="#241f26" strokeWidth="4" />
                  <circle cx="110" cy="176" r="20" fill="#ffffff" opacity="0.16" stroke="none" />
                  <circle cx="110" cy="176" r="12" fill="#ffffff" opacity="0.24" stroke="none" />
                  <circle cx="110" cy="176" r="5" fill="#ffffff" opacity="0.35" stroke="none" />
                  <circle cx="86" cy="192" r="7" fill="#ffffff" opacity="0.18" stroke="none" />
                  <circle cx="134" cy="156" r="7" fill="#ffffff" opacity="0.18" stroke="none" />
                </>) : null}
                {v.oTrack ? (<>
                  <path d="M110 146 v56" stroke={v.inner} strokeWidth="4" />
                  <path d="M94 144 h32" stroke={v.inner} strokeWidth="6" />
                  <path d="M74 156 h18 M128 156 h18" stroke="#f4efe6" strokeWidth="4" />
                </>) : null}
                {v.jChain ? (<>
                  <path d="M90 146 C98 160 122 160 130 146" fill="none" stroke="#e8c15a" strokeWidth="4" />
                  <circle cx="110" cy="158" r="4" fill="#e8c15a" stroke="none" />
                </>) : null}
              </g>
              <g className="head-sync">
                <g id="head">
                  <circle cx="58" cy="116" r="8" fill={v.skin} />
                  <circle cx="162" cy="116" r="8" fill={v.skin} />
                  <rect x="58" y="46" width="104" height="98" rx="34" fill={v.skin} />
                  {v.jEar ? (<>
                    <circle cx="58" cy="127" r="3.5" fill="#e8c15a" strokeWidth="3" />
                    <circle cx="162" cy="127" r="3.5" fill="#e8c15a" strokeWidth="3" />
                  </>) : null}
                </g>
                <g id="brows" stroke="none" fill="#241f26">
                  {v.bFlat ? (<>
                    <path d="M77 87 C84 83 94 83 101 86 L101 93 C94 89 84 89 77 94 Z" />
                    <path d="M143 87 C136 83 126 83 119 86 L119 93 C126 89 136 89 143 94 Z" />
                  </>) : null}
                  {v.bRaised ? (<>
                    <path d="M77 79 C84 74 94 74 101 78 L101 85 C94 80 84 80 77 86 Z" />
                    <path d="M143 79 C136 74 126 74 119 78 L119 85 C126 80 136 80 143 86 Z" />
                  </>) : null}
                  {v.bAngry ? (<>
                    <path d="M76 82 C84 83 94 87 101 92 L101 98 C94 92 84 88 76 88 Z" />
                    <path d="M144 82 C136 83 126 87 119 92 L119 98 C126 92 136 88 144 88 Z" />
                  </>) : null}
                  {v.bSad ? (<>
                    <path d="M76 94 C84 91 94 86 101 83 L101 89 C94 92 84 97 76 100 Z" />
                    <path d="M144 94 C136 91 126 86 119 83 L119 89 C126 92 136 97 144 100 Z" />
                  </>) : null}
                </g>
                <g id="eyes" stroke="none">
                  {v.eOpen ? (<>
                    <ellipse cx="88" cy="108" rx="13.5" ry="14" fill="#fbf8f4" />
                    <ellipse cx="132" cy="108" rx="13.5" ry="14" fill="#fbf8f4" />
                    <ellipse cx="89" cy="109" rx="8.6" ry="10" fill={v.eye} />
                    <ellipse cx="131" cy="109" rx="8.6" ry="10" fill={v.eye} />
                    <ellipse cx="89" cy="111" rx="4.3" ry="5.6" fill="#201c24" />
                    <ellipse cx="131" cy="111" rx="4.3" ry="5.6" fill="#201c24" />
                    <circle cx="85" cy="102" r="3.2" fill="#ffffff" />
                    <circle cx="127" cy="102" r="3.2" fill="#ffffff" />
                  </>) : null}
                  {v.eLid ? (<>
                    <path d="M75 104 h26 v-13 h-26 z M119 104 h26 v-13 h-26 z" fill={v.skin} />
                    <path d="M75 104 h26 M119 104 h26" stroke="#241f26" strokeWidth="4" strokeLinecap="round" />
                  </>) : null}
                  {v.eHappy ? (<>
                    <path d="M76 113 C82 99 96 99 102 113 M118 113 C124 99 138 99 144 113" fill="none" stroke="#241f26" strokeWidth="5" strokeLinecap="round" />
                  </>) : null}
                  {v.eWink ? (<>
                    <ellipse cx="88" cy="108" rx="13.5" ry="14" fill="#fbf8f4" />
                    <ellipse cx="89" cy="109" rx="8.6" ry="10" fill={v.eye} />
                    <ellipse cx="89" cy="111" rx="4.3" ry="5.6" fill="#201c24" />
                    <circle cx="85" cy="102" r="3.2" fill="#ffffff" />
                    <path d="M117 106 C123 115 139 115 145 106" fill="none" stroke="#241f26" strokeWidth="6" strokeLinecap="round" />
                  </>) : null}
                  {v.eHeart ? (<>
                    <path d="M88 119 C72 107 74 93 84 93 C89 93 88 98 88 100 C88 98 87 93 92 93 C102 93 104 107 88 119 Z" fill="#e0455e" />
                    <path d="M132 119 C116 107 118 93 128 93 C133 93 132 98 132 100 C132 98 131 93 136 93 C146 93 148 107 132 119 Z" fill="#e0455e" />
                  </>) : null}
                  {v.eX ? (<>
                    <path d="M79 99 L97 117 M97 99 L79 117 M123 99 L141 117 M141 99 L123 117" fill="none" stroke="#241f26" strokeWidth="6" strokeLinecap="round" />
                  </>) : null}
                </g>
                <g id="facial-hair" fill={v.hair} strokeWidth="3.5">
                  {v.fStubble ? (<>
                    <path d="M58 104 L58 110 C58 129 73 144 92 144 L128 144 C147 144 162 129 162 110 L162 104 C156 122 136 131 110 131 C84 131 64 122 58 104 Z" opacity="0.3" stroke="none" />
                  </>) : null}
                  {v.fMoustache ? (<>
                    <path d="M110 124 C104 117 92 116 88 123 C85 129 90 134 96 132 C102 130 106 127 110 127 C114 127 118 130 124 132 C130 134 135 129 132 123 C128 116 116 117 110 124 Z" />
                  </>) : null}
                  {v.fHandlebar ? (<>
                    <path d="M110 124 C104 117 92 116 88 123 C85 129 90 134 96 132 C102 130 106 127 110 127 C114 127 118 130 124 132 C130 134 135 129 132 123 C128 116 116 117 110 124 Z" />
                    <circle cx="84" cy="121" r="4.5" />
                    <circle cx="136" cy="121" r="4.5" />
                  </>) : null}
                  {v.fGoatee ? (<>
                    <path d="M110 124 C104 117 92 116 88 123 C85 129 90 134 96 132 C102 130 106 127 110 127 C114 127 118 130 124 132 C130 134 135 129 132 123 C128 116 116 117 110 124 Z" />
                    <path d="M100 139 C100 151 105 157 110 157 C115 157 120 151 120 139 C116 143 104 143 100 139 Z" />
                  </>) : null}
                  {v.fChinstrap ? (<>
                    <path d="M58 104 L58 110 C58 129 73 144 92 144 L128 144 C147 144 162 129 162 110 L162 104 C158 126 136 136 110 136 C84 136 64 126 58 104 Z" />
                  </>) : null}
                  {v.fChops ? (<>
                    <path d="M59 102 C59 126 66 140 78 146 C80 138 76 120 72 102 C66 99 61 99 59 102 Z" />
                    <path d="M161 102 C161 126 154 140 142 146 C140 138 144 120 148 102 C154 99 159 99 161 102 Z" />
                  </>) : null}
                  {v.fBeard ? (<>
                    <path d="M58 104 L58 112 C58 140 76 160 110 160 C144 160 162 140 162 112 L162 104 C156 122 136 131 110 131 C84 131 64 122 58 104 Z" />
                    <path d="M110 124 C104 117 92 116 88 123 C85 129 90 134 96 132 C102 130 106 127 110 127 C114 127 118 130 124 132 C130 134 135 129 132 123 C128 116 116 117 110 124 Z" />
                  </>) : null}
                </g>
                <g id="mouth">
                  {v.mNeutral ? (<>
                    <ellipse cx="110" cy="137" rx="5" ry="3.4" fill="#c47a72" stroke="none" />
                  </>) : null}
                  {v.mSmile ? (<>
                    <path d="M99 133 C104 144 116 144 121 133" fill="none" stroke="#241f26" strokeWidth="5" strokeLinecap="round" />
                  </>) : null}
                  {v.mLaugh ? (<>
                    <path d="M100 132 C103 146 117 146 120 132 Z" fill="#8c3b3b" />
                  </>) : null}
                  {v.mFrown ? (<>
                    <path d="M99 142 C104 132 116 132 121 142" fill="none" stroke="#241f26" strokeWidth="5" strokeLinecap="round" />
                  </>) : null}
                  {v.mGrit ? (<>
                    <path d="M98 129 h24 a3 3 0 0 1 3 3 v6 a3 3 0 0 1 -3 3 h-24 a3 3 0 0 1 -3 -3 v-6 a3 3 0 0 1 3 -3 z" fill="#8c3b3b" />
                    <path d="M104 130 v11 M110 130 v11 M116 130 v11" stroke="#f4efe6" strokeWidth="3" />
                  </>) : null}
                  {v.mOh ? (<>
                    <circle cx="110" cy="137" r="6.5" fill="#8c3b3b" />
                  </>) : null}
                  {v.mSmirk ? (<>
                    <path d="M100 135 C106 144 118 142 123 133" fill="none" stroke="#241f26" strokeWidth="5" strokeLinecap="round" />
                  </>) : null}
                </g>
                <g id="hair-front" fill={v.hair}>
                  {v.hLiberty ? (<>
                    <path d="M54 92 C54 74 58 60 66 50 L52 16 L80 42 L92 8 L102 40 L110 2 L118 40 L128 8 L140 42 L168 16 L154 50 C162 60 166 74 166 92 C146 74 74 74 54 92 Z" />
                  </>) : null}
                  {v.hAfro ? (<>
                    <path d="M110 6 C66 6 38 36 42 74 C44 96 54 110 66 112 C62 92 62 76 70 62 C82 50 138 50 150 62 C158 76 158 92 154 112 C166 110 176 96 178 74 C182 36 154 6 110 6 Z" />
                  </>) : null}
                  {v.hSbuns ? (<>
                    <path d="M52 96 C50 54 68 26 110 26 C152 26 170 54 168 96 L156 104 L149 92 C145 66 130 56 110 56 C90 56 75 66 71 92 L64 104 Z" />
                    <path d="M110 28 v24" fill="none" stroke={v.hairDark} strokeWidth="4" />
                  </>) : null}
                  {v.hBraids ? (<>
                    <path d="M52 96 C50 54 68 26 110 26 C152 26 170 54 168 96 L156 104 L149 92 C145 66 130 56 110 56 C90 56 75 66 71 92 L64 104 Z" />
                    <path d="M110 28 v24" fill="none" stroke={v.hairDark} strokeWidth="4" />
                  </>) : null}
                  {v.hHalf ? (<>
                    <path d="M58 84 C56 48 78 26 110 26 C144 26 164 48 164 82 C164 108 160 128 152 142 C148 130 148 108 144 92 C140 72 124 58 100 58 C82 60 66 70 58 84 Z" />
                  </>) : null}
                  {v.hSwept ? (<>
                    <path d="M42 100 C38 56 62 20 110 20 C158 20 182 56 178 100 C172 88 168 78 162 70 C148 58 118 54 96 62 C78 70 62 82 42 100 Z" />
                    <path d="M148 40 C170 62 170 114 158 146 C154 156 143 153 144 142 C150 112 148 74 136 54 Z" />
                  </>) : null}
                  {v.hSpiky ? (<>
                    <path d="M46 94 C44 52 64 22 110 22 C156 22 176 52 174 94 L160 104 L152 72 L142 92 L130 62 L118 86 L106 58 L94 84 L82 62 L70 90 L60 104 Z" />
                  </>) : null}
                  {v.hQuiff ? (<>
                    <path d="M56 92 C52 52 76 22 112 22 C138 22 158 34 164 56 C156 46 144 40 130 40 C110 34 90 42 78 58 C66 70 58 80 56 92 Z" />
                  </>) : null}
                  {v.hUnder ? (<>
                    <path d="M58 82 C56 48 78 26 110 26 C144 26 166 46 164 74 C150 56 122 48 98 56 C78 62 66 70 58 82 Z" />
                  </>) : null}
                  {v.hBuzz ? (<>
                    <path d="M56 88 C56 52 72 28 110 28 C148 28 164 52 164 88 C150 79 130 75 110 75 C90 75 70 79 56 88 Z" />
                  </>) : null}
                  {v.hSlick ? (<>
                    <path d="M58 84 C56 48 78 24 110 24 C142 24 164 48 162 84 C154 62 136 52 110 52 C84 52 66 62 58 84 Z" />
                    <path d="M78 46 C92 36 128 36 142 46 M72 62 C90 50 130 50 148 62" fill="none" stroke={v.hairLight} strokeWidth="4" strokeLinecap="round" opacity="0.4" />
                  </>) : null}
                  {v.hCurtain ? (<>
                    <path d="M56 94 C54 54 76 26 110 28 C104 44 92 52 84 62 C72 74 62 84 56 94 Z" />
                    <path d="M164 94 C166 54 144 26 110 28 C116 44 128 52 136 62 C148 74 158 84 164 94 Z" />
                  </>) : null}
                  {v.hDreads ? (<>
                    <rect x="50" y="74" width="14" height="98" rx="7" />
                    <rect x="156" y="74" width="14" height="98" rx="7" />
                    <path d="M52 96 C50 54 72 26 110 26 C148 26 170 54 168 96 L156 88 C150 60 134 50 110 50 C86 50 70 60 64 88 Z" />
                  </>) : null}
                  {v.hRows ? (<>
                    <path d="M56 88 C56 52 76 28 110 28 C144 28 164 52 164 88 C150 78 130 74 110 74 C90 74 70 78 56 88 Z" />
                    <path d="M68 84 C76 58 94 42 110 34 M88 80 C94 58 103 44 110 34 M132 80 C126 58 117 44 110 34 M152 84 C144 58 126 42 110 34" fill="none" stroke={v.hairLight} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
                  </>) : null}
                  {v.hLong ? (<>
                    <path d="M40 100 C36 56 60 20 110 20 C160 20 184 56 180 100 C174 84 168 74 160 66 C144 54 76 54 60 66 C52 74 46 84 40 100 Z" />
                  </>) : null}
                  {v.hMessy ? (<>
                    <path d="M46 96 C42 52 66 20 110 22 C152 24 178 52 174 96 L162 84 L156 62 L146 82 L134 58 L120 80 L108 56 L96 78 L84 60 L72 84 L58 66 Z" />
                  </>) : null}
                  {v.hFlat ? (<>
                    <path d="M56 84 L56 32 C56 26 60 22 66 22 L154 22 C160 22 164 26 164 32 L164 84 C152 72 130 66 110 66 C90 66 68 72 56 84 Z" />
                  </>) : null}
                  {v.hMullet ? (<>
                    <path d="M58 86 C58 52 74 28 110 28 C146 28 162 52 162 86 C150 76 130 72 110 72 C90 72 70 76 58 86 Z" />
                  </>) : null}
                  {v.hBald ? (<>
                    <path d="M58 96 C56 72 64 54 78 46 C72 62 70 78 72 96 C74 108 76 116 80 124 C70 122 62 112 58 96 Z" />
                    <path d="M162 96 C164 72 156 54 142 46 C148 62 150 78 148 96 C146 108 144 116 140 124 C150 122 158 112 162 96 Z" />
                  </>) : null}
                  {v.hMohawk ? (<>
                    <path d="M94 14 C84 36 82 60 88 82 L132 82 C138 60 136 36 126 14 C120 26 100 26 94 14 Z" />
                  </>) : null}
                  {v.hCurls ? (<>
                    <path d="M34 82 C24 60 40 36 62 32 C70 18 92 10 110 14 C130 10 150 20 158 34 C180 40 192 62 184 84 C176 70 160 62 148 64 C138 52 124 48 110 52 C94 48 78 54 70 66 C56 64 44 72 34 82 Z" />
                    <path d="M56 62 C70 48 90 44 104 52 M120 42 C138 44 154 58 160 76 M46 98 C50 82 60 70 74 62 M152 100 C162 86 164 70 156 58 M90 28 C104 20 122 20 134 30" fill="none" stroke={v.hairLight} strokeWidth="5" strokeLinecap="round" opacity="0.45" />
                  </>) : null}
                  {v.hBun ? (<>
                    <path d="M52 96 C50 54 68 26 110 26 C152 26 170 54 168 96 L156 104 L149 92 C145 66 130 56 110 56 C90 56 75 66 71 92 L64 104 Z" />
                  </>) : null}
                  {v.hTail ? (<>
                    <path d="M48 96 C46 54 68 22 110 22 C152 22 174 54 172 96 L158 104 C152 72 136 58 110 58 C84 58 68 72 62 104 Z" />
                  </>) : null}
                </g>
                <g id="mask-front">
                  {v.mMouse ? (<>
                    <circle cx="66" cy="52" r="20" fill={v.maskFill} />
                    <circle cx="154" cy="52" r="20" fill={v.maskFill} />
                    <circle cx="66" cy="52" r="9" fill="#e8a8b8" stroke="none" />
                    <circle cx="154" cy="52" r="9" fill="#e8a8b8" stroke="none" />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                    <ellipse cx="110" cy="126" rx="26" ry="16" fill="#f2e6da" stroke="none" />
                    <ellipse cx="88" cy="104" rx="9" ry="11" fill="#241f26" stroke="none" />
                    <ellipse cx="132" cy="104" rx="9" ry="11" fill="#241f26" stroke="none" />
                    <circle cx="85" cy="100" r="3" fill="#ffffff" stroke="none" />
                    <circle cx="129" cy="100" r="3" fill="#ffffff" stroke="none" />
                    <ellipse cx="110" cy="122" rx="6" ry="4.5" fill="#3a3038" />
                    <path d="M84 128 L60 122 M84 132 L60 134 M136 128 L160 122 M136 132 L160 134" fill="none" stroke="#241f26" strokeWidth="2.5" />
                    <path d="M102 134 C106 140 114 140 118 134" fill="none" stroke="#241f26" strokeWidth="3.5" />
                  </>) : null}
                  {v.mCat ? (<>
                    <path d="M60 66 L54 26 L86 46 Z" fill={v.maskFill} />
                    <path d="M160 66 L166 26 L134 46 Z" fill={v.maskFill} />
                    <path d="M63 60 L59 36 L80 49 Z" fill="#e8a8b8" stroke="none" />
                    <path d="M157 60 L161 36 L140 49 Z" fill="#e8a8b8" stroke="none" />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                    <path d="M76 104 C82 94 96 94 100 106 C96 114 82 114 76 104 Z" fill="#c9e04a" stroke="none" />
                    <path d="M144 104 C138 94 124 94 120 106 C124 114 138 114 144 104 Z" fill="#c9e04a" stroke="none" />
                    <ellipse cx="89" cy="104" rx="3.5" ry="7" fill="#241f26" stroke="none" />
                    <ellipse cx="131" cy="104" rx="3.5" ry="7" fill="#241f26" stroke="none" />
                    <path d="M104 122 L116 122 L110 129 Z" fill="#e8a8b8" />
                    <path d="M110 129 C110 135 104 138 99 136 M110 129 C110 135 116 138 121 136" fill="none" stroke="#241f26" strokeWidth="3.5" />
                    <path d="M86 122 L62 118 M86 128 L62 130 M134 122 L158 118 M134 128 L158 130" fill="none" stroke="#241f26" strokeWidth="2.5" />
                  </>) : null}
                  {v.mBear ? (<>
                    <circle cx="70" cy="54" r="17" fill={v.maskFill} />
                    <circle cx="150" cy="54" r="17" fill={v.maskFill} />
                    <circle cx="70" cy="54" r="8" fill="#c9a684" stroke="none" />
                    <circle cx="150" cy="54" r="8" fill="#c9a684" stroke="none" />
                    <rect x="58" y="46" width="104" height="98" rx="34" fill={v.maskFill} />
                    <ellipse cx="110" cy="124" rx="24" ry="18" fill="#c9a684" stroke="none" />
                    <circle cx="88" cy="102" r="6" fill="#241f26" stroke="none" />
                    <circle cx="132" cy="102" r="6" fill="#241f26" stroke="none" />
                    <ellipse cx="110" cy="118" rx="8" ry="6" fill="#3a3038" />
                    <path d="M110 124 v6 M102 136 C106 141 114 141 118 136" fill="none" stroke="#241f26" strokeWidth="3.5" />
                  </>) : null}
                  {v.mFrog ? (<>
                    <circle cx="80" cy="46" r="17" fill={v.maskFill} />
                    <circle cx="140" cy="46" r="17" fill={v.maskFill} />
                    <circle cx="80" cy="46" r="10" fill="#fbf8f4" stroke="none" />
                    <circle cx="140" cy="46" r="10" fill="#fbf8f4" stroke="none" />
                    <circle cx="80" cy="48" r="4.5" fill="#241f26" stroke="none" />
                    <circle cx="140" cy="48" r="4.5" fill="#241f26" stroke="none" />
                    <path d="M80 34 a17 17 0 0 1 17 15 L63 49 a17 17 0 0 1 17 -15 Z" fill={v.maskFill} strokeWidth="4" />
                    <path d="M140 34 a17 17 0 0 1 17 15 L123 49 a17 17 0 0 1 17 -15 Z" fill={v.maskFill} strokeWidth="4" />
                    <rect x="58" y="52" width="104" height="92" rx="34" fill={v.maskFill} />
                    <circle cx="92" cy="112" r="4" fill="#241f26" stroke="none" />
                    <circle cx="128" cy="112" r="4" fill="#241f26" stroke="none" />
                    <path d="M88 128 C98 138 122 138 132 128" fill="none" stroke="#241f26" strokeWidth="4" />
                    <circle cx="80" cy="126" r="5" fill="#e88a6a" stroke="none" opacity="0.6" />
                    <circle cx="140" cy="126" r="5" fill="#e88a6a" stroke="none" opacity="0.6" />
                  </>) : null}
                  {v.mRobot ? (<>
                    <path d="M110 22 v14" stroke="#8d8598" strokeWidth="4" />
                    <circle cx="110" cy="18" r="6" fill={v.accent} />
                    <rect x="58" y="46" width="104" height="98" rx="18" fill={v.maskFill} />
                    <rect x="70" y="90" width="80" height="26" rx="13" fill="#241f26" />
                    <circle cx="90" cy="103" r="7" fill="#7de0ff" stroke="none" />
                    <circle cx="130" cy="103" r="7" fill="#7de0ff" stroke="none" />
                    <path d="M92 130 h36" stroke="#241f26" strokeWidth="4" />
                    <path d="M96 126 v8 M104 126 v8 M112 126 v8 M120 126 v8" stroke="#241f26" strokeWidth="2.5" />
                    <rect x="52" y="96" width="10" height="22" rx="5" fill="#8d8598" />
                    <rect x="158" y="96" width="10" height="22" rx="5" fill="#8d8598" />
                  </>) : null}
                </g>
                <g id="accessory">
                  {v.aBucket ? (<>
                    <path d="M66 64 C66 40 86 28 110 28 C134 28 154 40 154 64 Z" fill={v.accent} />
                    <path d="M54 64 C78 56 142 56 166 64 L158 82 C134 74 86 74 62 82 Z" fill={v.accent} />
                  </>) : null}
                  {v.aBandana ? (<>
                    <path d="M62 70 C88 62 132 62 158 70 L158 84 C132 76 88 76 62 84 Z" fill={v.accent} />
                    <circle cx="92" cy="74" r="2" fill="#f4efe6" stroke="none" />
                    <circle cx="110" cy="72" r="2" fill="#f4efe6" stroke="none" />
                    <circle cx="128" cy="74" r="2" fill="#f4efe6" stroke="none" />
                  </>) : null}
                  {v.aBand ? (<>
                    <path d="M62 72 C88 64 132 64 158 72 L158 84 C132 76 88 76 62 84 Z" fill="#f4efe6" />
                  </>) : null}
                  {v.aCap ? (<>
                    <path d="M56 84 C56 50 78 32 110 32 C142 32 164 50 164 84 C140 74 80 74 56 84 Z" fill={v.accent} />
                    <path d="M50 84 C76 74 146 74 170 84 C170 96 148 102 110 102 C72 102 50 96 50 84 Z" fill={v.accent} />
                    <circle cx="110" cy="33" r="5" fill={v.accent} />
                  </>) : null}
                  {v.aBeanie ? (<>
                    <path d="M56 88 C56 50 78 30 110 30 C142 30 164 50 164 88 Z" fill={v.accent} />
                    <rect x="52" y="84" width="116" height="18" rx="8" fill={v.inner} />
                    <circle cx="110" cy="26" r="9" fill={v.inner} />
                  </>) : null}
                  {v.aGlasses ? (<>
                    <rect x="71" y="94" width="34" height="28" rx="9" fill="#ffffff" fillOpacity="0.22" />
                    <rect x="115" y="94" width="34" height="28" rx="9" fill="#ffffff" fillOpacity="0.22" />
                    <path d="M105 106 h10 M71 103 L57 106 M149 103 L163 106" fill="none" stroke="#241f26" strokeWidth="4" />
                  </>) : null}
                  {v.aShades ? (<>
                    <rect x="70" y="94" width="36" height="28" rx="9" fill="#2a2630" />
                    <rect x="114" y="94" width="36" height="28" rx="9" fill="#2a2630" />
                    <path d="M105 105 h10 M70 101 L56 104 M150 101 L164 104" fill="none" stroke="#241f26" strokeWidth="4" />
                    <path d="M76 113 L86 99" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="5" />
                    <path d="M120 113 L130 99" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="5" />
                  </>) : null}
                  {v.aPhones ? (<>
                    <path d="M56 100 C54 52 80 28 110 28 C140 28 166 52 164 100" fill="none" stroke="#241f26" strokeWidth="14" />
                    <path d="M56 100 C54 52 80 28 110 28 C140 28 166 52 164 100" fill="none" stroke={v.accent} strokeWidth="7" />
                    <rect x="44" y="98" width="24" height="38" rx="10" fill={v.accent} />
                    <rect x="152" y="98" width="24" height="38" rx="10" fill={v.accent} />
                  </>) : null}
                </g>
              </g>
            </g>
        </>
      )}
    </svg>
  );
}
